import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader, EmptyState } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import SaleDialog from "@/components/vi/SaleDialog";
import { CheckinDialog } from "@/components/vi/FieldDialogs";
import OfflineBar from "@/components/vi/OfflineBar";
import ScanButton from "@/components/vi/QrScanner";
import ViList from "@/components/vi/ViList";
import api from "@/lib/api";
import { cachedGet } from "@/lib/offline";
import { call, money, VI_MANAGERS } from "@/lib/vi";
import { Store, Bike, MapPin, ShoppingCart, Phone, Crosshair } from "lucide-react";

function FixPosition({ client, onDone }) {
  const [busy, setBusy] = useState(false);
  const fix = () => {
    if (!navigator.geolocation) return;
    setBusy(true);
    navigator.geolocation.getCurrentPosition(async (p) => {
      const r = await call(() => api.post(`/itinerant/clients/${client.id}/position`, { lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }), "Position de la boutique enregistrée");
      setBusy(false); if (r) onDone();
    }, () => { setBusy(false); window.alert("Position indisponible : autorisez la localisation"); }, { enableHighAccuracy: true, timeout: 15000 });
  };
  return (
    <div className="rounded-xl border bg-white p-3 mb-3 flex items-center justify-between gap-2" data-testid="scan-gps">
      <span className="text-xs">{client.gps ? <>GPS : <span className="font-mono">{client.gps.lat.toFixed(5)}, {client.gps.lng.toFixed(5)}</span> ({client.gps.source || "manuel"})</> : <span className="text-orange-600">Position GPS non enregistrée</span>}</span>
      <Button size="sm" variant="outline" onClick={fix} disabled={busy} data-testid="scan-fix-position"><Crosshair className="h-4 w-4 mr-1" />Fixer la position ici</Button>
    </div>
  );
}

const Info = ({ items }) => (
  <div className="grid grid-cols-2 gap-2 text-sm">
    {items.map(([l, v]) => <div key={l} className="rounded-lg border bg-white p-2"><p className="text-[10px] uppercase font-mono text-slate-400">{l}</p><p className="font-medium">{v || "—"}</p></div>)}
  </div>
);

export function ViScanClient() {
  const { id } = useParams();
  const { user, role } = useAuth();
  const [c, setC] = useState(undefined);
  const [tour, setTour] = useState(null);
  const [dlg, setDlg] = useState("");
  const load = () => {
    cachedGet("/itinerant/clients").then((l) => setC(l.find((x) => x.id === id) || null)).catch(() => setC(null));
    cachedGet("/itinerant/my-tour").then((d) => setTour(d.tour)).catch(() => setTour(null));
  };
  useEffect(load, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (c === undefined) return <Loader />;
  if (!c) return <EmptyState icon={Store} title="Boutique introuvable" subtitle="Ce client n'existe pas ou n'est pas rattaché à vos circuits." />;
  const live = tour?.statut === "En tournée";
  const openVisit = (tour?.visits || []).find((v) => v.statut === "En cours");
  return (
    <div className="max-w-xl mx-auto" data-testid="scan-client">
      <OfflineBar />
      <div className="rounded-2xl bg-brand-700 text-white p-4 mb-3">
        <p className="text-[11px] uppercase font-mono opacity-70">Boutique · {c.numero}</p>
        <p className="font-head text-xl font-bold" data-testid="scan-client-name">{c.nom}</p>
        <p className="text-sm opacity-90">{[c.type_boutique, c.zone, c.adresse].filter(Boolean).join(" · ")}</p>
        {c.niveau && <div className="mt-2"><StatusBadge value={c.niveau} /></div>}
      </div>
      <Info items={[["Téléphone", c.telephone], ["Dernier achat", c.dernier_achat], ["Dernière visite", c.derniere_visite], ["CA cumulé", money(c.ca_cumule)],
        ["Encours", money(c.encours)], ["Crédit disponible", money(c.credit_disponible)], ["Produits habituels", c.produits_habituels], ["Fréquence", c.frequence_achat]]} />
      <div className="mt-3"><FixPosition client={c} onDone={load} /></div>
      <div className="grid grid-cols-2 gap-2 my-4">
        {live && !openVisit && <Button className="h-14 text-base font-semibold bg-brand-700 text-white" onClick={() => setDlg("in")} data-testid="scan-checkin"><MapPin className="h-5 w-5 mr-2" />CHECK-IN</Button>}
        {live && <Button className="h-14 text-base font-semibold bg-brand-600 text-white" onClick={() => setDlg("sale")} data-testid="scan-sale"><ShoppingCart className="h-5 w-5 mr-2" />VENDRE</Button>}
        {c.telephone && <a href={`tel:${c.telephone}`}><Button variant="outline" className="h-14 w-full" data-testid="scan-call"><Phone className="h-5 w-5 mr-2" />APPELER</Button></a>}
        <ScanButton big />
      </div>
      {!live && <p className="text-xs text-slate-500 mb-3" data-testid="scan-no-tour">Aucune tournée en cours : check-in et vente disponibles uniquement pendant une tournée.</p>}
      {openVisit && <p className="text-xs text-slate-600 mb-3">Visite en cours : <b>{openVisit.cible}</b> — <Link to="/vi/ma-tournee" className="text-brand-700 underline">terminer depuis Ma tournée</Link></p>}
      {role !== "conducteur_vendeur" && <Link to="/clients" className="text-xs text-brand-700 hover:underline">Fiche client complète →</Link>}
      {tour && <SaleDialog tour={tour} open={dlg === "sale"} onOpenChange={() => setDlg("")} onDone={load} canForce={VI_MANAGERS.includes(user.role)} presetClientId={c.id} />}
      {tour && <CheckinDialog tour={tour} open={dlg === "in"} onOpenChange={() => setDlg("")} onDone={() => { setDlg(""); load(); }} presetCible={`c:${c.id}`} />}
    </div>
  );
}

export function ViScanTricycle() {
  const { id } = useParams();
  const [t, setT] = useState(undefined);
  const [tour, setTour] = useState(null);
  useEffect(() => {
    cachedGet("/itinerant/tricycles").then((l) => setT(l.find((x) => x.id === id) || null)).catch(() => setT(null));
    cachedGet("/itinerant/tournees").then((l) => setTour(l.find((x) => x.tricycle_id === id && !["Clôturée", "Annulée"].includes(x.statut)) || null)).catch(() => {});
  }, [id]);
  if (t === undefined) return <Loader />;
  if (!t) return <EmptyState icon={Bike} title="Tricycle introuvable" subtitle="Ce tricycle n'existe pas ou ne vous est pas affecté." />;
  return (
    <div className="max-w-xl mx-auto" data-testid="scan-tricycle">
      <OfflineBar />
      <div className="rounded-2xl bg-brand-700 text-white p-4 mb-3">
        <p className="text-[11px] uppercase font-mono opacity-70">Tricycle</p>
        <p className="font-head text-xl font-bold" data-testid="scan-tri-code">TRICYCLE {t.code}</p>
        <p className="text-sm opacity-90">{t.immatriculation} · {t.marque} {t.modele}</p>
        <div className="mt-2"><StatusBadge value={t.statut} /></div>
      </div>
      <Info items={[["Conducteur", t.conducteur_name], ["Circuit", t.circuit_name], ["Entrepôt", t.entrepot_name], ["Kilométrage", `${t.kilometrage || 0} km`],
        ["Assurance", t.assurance_fin], ["Visite technique", t.visite_technique_fin]]} />
      <div className="rounded-xl border bg-white p-3 my-4" data-testid="scan-tri-tour">
        {tour ? <><p className="text-sm">Tournée du jour : <b>{tour.numero}</b> · {tour.circuit?.nom} · {tour.date}</p>
          <div className="flex items-center gap-2 mt-2"><StatusBadge value={tour.statut} /><span className="text-xs font-mono">CA {money(tour.ca)}</span>
            <Link to={`/vi/tournees/${tour.id}`} className="ml-auto"><Button size="sm" className="bg-brand-600 text-white" data-testid="scan-tri-open-tour">Ouvrir la tournée</Button></Link></div></>
          : <p className="text-sm text-slate-500">Aucune tournée en cours pour ce tricycle.</p>}
      </div>
      <ScanButton big className="w-full" />
    </div>
  );
}

export function ViConflicts() {
  const { role } = useAuth();
  const [key, setKey] = useState(0);
  const resolve = async (o) => {
    const note = window.prompt("Décision / note de traitement");
    if (note === null) return;
    const r = await call(() => api.post(`/itinerant/sync/conflicts/${o.op_id}/resolve`, { note }), "Conflit traité");
    if (r) setKey((k) => k + 1);
  };
  const T = { sale: "Vente", checkin: "Check-in", checkout: "Check-out", quote: "Devis" };
  return <ViList title="Conflits de synchronisation" subtitle="Opérations faites hors ligne refusées au retour du réseau (stock insuffisant, tournée terminée…)" endpoint="/itinerant/sync/conflicts" testid="vi-conflicts" reloadKey={key}
    columns={[{ key: "offline_at", label: "Fait le", render: (o) => (o.offline_at || "").slice(0, 16).replace("T", " ") }, { key: "type", label: "Opération", render: (o) => T[o.type] || o.type },
      { key: "label", label: "Client / cible" }, { key: "vendeur_name", label: "Vendeur" }, { key: "detail", label: "Motif du refus", render: (o) => <span className="text-rose-600 text-xs">{o.detail}</span> },
      { key: "resolu", label: "Statut", render: (o) => <StatusBadge value={o.resolu ? "Traité" : "À traiter"} /> },
      { key: "a", label: "", render: (o) => !o.resolu && VI_MANAGERS.includes(role) && <Button size="sm" variant="outline" onClick={() => resolve(o)} data-testid={`conflict-resolve-${o.op_id}`}>Marquer traité</Button> }]} />;
}
